/* import __COLOCATED_TEMPLATE__ from './list-keyboard-event-handler.hbs'; */
/**
## List Keyboard Event Handler

Use this component to wrap any list to handle keyboard arrow and enter events for highlight (focus)
and selection.

```example
@title Basic Usage

@hbs
{{#list-keyboard-event-handler itemFocusedClass="popover-list__item--is-focused"
                                itemClass="popover-list__item"
                                class="keyboard-event-handler"}}
      <div class="popover-list__item hyperlink">Option 1</div>
      <div class="popover-list__item hyperlink">Option 2</div>
      <div class="popover-list__item hyperlink">Option 3</div>
{{/list-keyboard-event-handler}}
```

### Parameters
 * @param {string} [itemFocusedClass] className to be used to apply active / focused styling to the list item that is focused
 * @param {string} [itemClass] className used to identify each individual item in the dom
 * @param {list} [items] list of actual objects being displayed in the list. This is used to pass back to the enterPressed action so that your
 *                         encompassing component can act on the actual object instead of only the dom.
 * @param {action} [enterPressed] action to be called when enter key is pressed. This will be passed the following parameters:
 *    Item: If list of items passed, use the actual item from the list, if not, pass the dom object
 *    Index: Index of the item in the list
 *    Event: Keypress event in case you want to stopPropagation.
*/
import Component from '@ember/component';
import { KEY_CODES } from '@square/glass-ui/utils/keyboard-support';
import { isEmpty } from '@ember/utils';

// eslint-disable-next-line ember/no-classic-classes
export default Component.extend({
  itemFocusedClass: null,
  itemClass: null,
  items: null,
  highlightedIndex: -1,
  domItems: null,
  didRender(...args) {
    this._super(...args);
    this.set('domItems', this.element.querySelectorAll(`.${this.itemClass}`));
  },
  // event handlers
  keyDown(e) {
    switch (e.keyCode) {
      case KEY_CODES.DOWN:
        this.highlightNext(e);
        break;
      case KEY_CODES.UP:
        this.highlightPrevious(e);
        break;
      case KEY_CODES.ENTER:
        if (this.enterPressed && this.highlightedIndex !== -1) {
          let selectedItem = this.get('domItems')[this.highlightedIndex];
          if (!isEmpty(this.items)) {
            selectedItem = this.items[this.highlightedIndex];
          }
          this.enterPressed(selectedItem, this.highlightedIndex, e);
        }
        break;
      default:
        break;
    }
  },
  highlightNext(e) {
    let nextIndex = this.highlightedIndex + 1;
    if (nextIndex >= this.get('domItems.length')) {
      nextIndex = 0;
    }
    this.highlightIndex(nextIndex);
    e.stopPropagation();
    e.preventDefault();
  },
  highlightPrevious(e) {
    let previousIndex = this.highlightedIndex - 1;
    if (previousIndex < 0) {
      previousIndex = this.get('domItems.length') - 1;
    }
    this.highlightIndex(previousIndex);
    e.stopPropagation();
    e.preventDefault();
  },
  highlightIndex(index) {
    this.set('highlightedIndex', index);
    const activeItem = this.element.querySelector(`.${this.itemFocusedClass}`);
    if (activeItem) {
      activeItem.classList.remove(this.itemFocusedClass);
    }
    const items = this.element.querySelectorAll(`.${this.itemClass}`);
    items.item(index).classList.add(this.itemFocusedClass);
  }
});